import { DataSourceMode, DropdownItem, DropdownSelectionEvent, LookupModelSearchEvent, SwitchProps, TextboxProps, ValidationResult } from "@mcleod/components";
import { AutogenLayoutMultiUpdateFieldPrompt } from "./autogen/AutogenLayoutMultiUpdateFieldPrompt";
import { CompanySettings, ModelRow, StringUtil } from "@mcleod/core";
import { CommonContextMenuItems } from "@mcleod/general";

const FREEFORM: Partial<TextboxProps> = {
    lookupModel: null,
    lookupModelLayout: null,
    lookupModelResultField: null,
    lookupModelDisplayField: null,
    lookupModelAllowFreeform: true,
    quickInfoLayout: null
}

const BR_STATUS_LM: Partial<TextboxProps> = {
    lookupModel: "lme/powerbroker/brokerage-status",
    lookupModelLayout: "lme/powerbroker/BrokerageStatusLookup",
    lookupModelLayoutWidth: 375,
    lookupModelResultField: "id",
    lookupModelDisplayField: "descr",
    lookupModelAllowFreeform: false,
    quickInfoLayout: null
}

const CARRIER_CONTACT_LM: Partial<TextboxProps> = {
    lookupModel: "lme/general/contact",
    lookupModelLayout: "lme/general/ContactLookup",
    lookupModelLayoutWidth: null,
    lookupModelDisplayField: "name",
    lookupModelResultField: "id",
    lookupModelAllowFreeform: false,
    quickInfoLayout: null
}

const USER_LM: Partial<TextboxProps> = {
    lookupModel: "common/users",
    lookupModelLayout: "common/UserLookup",
    lookupModelLayoutWidth: null,
    lookupModelDisplayField: "id",
    lookupModelResultField: "id",
    lookupModelAllowFreeform: false,
    quickInfoLayout: null
}

const REV_CODE_LM: Partial<TextboxProps> = {
    lookupModel: "lme/ar/revenue-code",
    lookupModelLayout: "lme/ar/RevenueCodeLookup",
    lookupModelLayoutWidth: null,
    lookupModelResultField: "id",
    lookupModelDisplayField: "descr",
    lookupModelAllowFreeform: false,
    quickInfoLayout: "lme/ar/RevenueCodeQuickInfo"
}

const ORDER_TYPE_LM: Partial<TextboxProps> = {
    lookupModel: "lme/dispatch/order-type",
    lookupModelLayout: "lme/dispatch/OrderTypeLookup",
    lookupModelLayoutWidth: null,
    lookupModelResultField: "id",
    lookupModelDisplayField: "descr",
    lookupModelAllowFreeform: false,
    quickInfoLayout: "lme/powerbroker/OrderTypeQuickInfo"
}

const MOVEMENT_TYPE_OPTIONS: Partial<TextboxProps> = {
    items: [
        new DropdownItem("Y", "Convert to Brokerage"),
        new DropdownItem("N", "Convert to Asset")
    ],
    allowDropdownBlank: false,
}

export enum Fields {
    BOLNumber = "orders.blnum",
    BrokerageStatus = "movement.brokerage_status",
    CarrierDispatchContact = "movement.carrier_contact",
    CarrierSalesRep = "movement.dispatcher_user_id",
    ConsigneeReferenceNumber = "orders.consignee_refno",
    MovementType = "movement.brokerage",
    OperationsUser = "orders.operations_user",
    OrderType = "orders.order_type_id",
    PlanningComment = "orders.planning_comment",
    RevenueCode = "orders.revenue_code_id"
}

export class MultiUpdateFieldPrompt extends AutogenLayoutMultiUpdateFieldPrompt {

    private _selectedData: ModelRow[] = [];
    private modelPath: string; // can be used to switch field names based on the datasource
    private beforeSearchListener: (event: LookupModelSearchEvent) => void;
    private maxLengthValidationCallback: (value: string, mode: DataSourceMode) => ValidationResult;
    private booleanFields = [
    ];

    textboxFieldAfterDropdownSelection(event: DropdownSelectionEvent) {
        const field = event.selection.value;
        const booleanField = this.isBooleanField(field);
        this.textboxFieldValue.visible = !booleanField;
        this.switchFieldValue.visible = booleanField;
        if (booleanField) {
            this.setupBooleanField(field);
        } else {
            this.setupTextField(field);
        }
    }

    isBooleanField(field: string): boolean {
        return this.booleanFields.some((bField) => bField === field);
    }

    setupTextField(field: string) {
        this.textboxFieldValue.clear();
        this.textboxFieldValue.enabled = this.validateByField(field);
        if (this.textboxFieldValue.enabled) {
            const lookup = this.mapFieldToLookup(field);
            this.textboxFieldValue.setProps(lookup);

            if (this.maxLengthValidationCallback != null) {
                this.textboxFieldValue.addlValidationCallback = null;
                this.maxLengthValidationCallback = null;
            }
            const maxLength = this.mapFieldToMaxLength(field);
            if (maxLength > 0) {
                this.maxLengthValidationCallback = (value: string, mode: DataSourceMode) => {
                    if (value.length > maxLength) {
                        return { isValid: false, validationMessage: `Maximum length is ${maxLength} characters.`, component: this.textboxFieldValue };
                    }
                    return { isValid: true, component: this.textboxFieldValue };
                };
                this.textboxFieldValue.addlValidationCallback = this.maxLengthValidationCallback;
            }
            this.addlSetup(field)
        }
    }

    setupBooleanField(field: string) {
        this.switchFieldValue.checked = false;
        this.switchFieldValue.setProps(this.getSwitchProps(field))
        this.switchFieldValue.enabled = this.validateByField(field);
        if (this.switchFieldValue.enabled) {
            this.addlSetup(field);
        }
    }

    addlSetup(field: string) {
        switch (field) {
            case Fields.MovementType: this.movementTypeSetup(); break;
        }
    }

    validateByField(field: string): boolean {
        this.textboxFieldValue.tooltip = null;
        if (this.beforeSearchListener != null) {
            if (this.textboxFieldValue.hasListener({listName: "_lookupListeners"}, this.beforeSearchListener)) {
                this.textboxFieldValue.removeBeforeLookupModelSearchListener(this.beforeSearchListener);
            }
            this.beforeSearchListener = null;
        }
        switch (field) {
            case Fields.CarrierDispatchContact: return this.carrierDispatchContactValidation() && this.updateMovementValidation();
            case Fields.CarrierSalesRep: return this.carrierSalesRepValidation() && this.updateMovementValidation();
            case Fields.OperationsUser: return this.operationsUserValidation();
            case Fields.BrokerageStatus: return this.allBrokerageValidation() && this.updateMovementValidation();
            case Fields.MovementType: return this.movementTypeValidation();
            default: return true;
        }
    }

    updateMovementValidation(): boolean {
        const updateMovementFilter = (row: ModelRow) => !CommonContextMenuItems.hasUpdateMovementPermissions(row);
        if (this.selectedData.some(updateMovementFilter)) {
            this.textboxFieldValue.disabledTooltip = "At least one selected movement is invalid for this update.";
            return false;
        }
    }

    carrierDispatchContactValidation(): boolean {
        const brokerageOnlyValidation = this.allBrokerageValidation();
        if (!brokerageOnlyValidation) return false;

        const availableFilter = (row: ModelRow) => "A" === row.getString("move_status");
        if (this.selectedData.some(availableFilter)) {
            this.textboxFieldValue.disabledTooltip = "All selected movements must be covered, delivered, or in progress.";
            return false;
        }

        const carrierIds = new Set(this.selectedData.map((row: ModelRow) => row.get("carrier_id", row.get("override_payee_id", null))));
        if (carrierIds.size > 1) {
            this.textboxFieldValue.disabledTooltip = "All selected movements must have the same carrier.";
            return false;
        }

        const payeeId = carrierIds.values().next().value;
        this.beforeSearchListener = (event: LookupModelSearchEvent) => {
            if (payeeId == null) {
                event.preventDefault();
            } else {
                event.filter.parent_row_id = payeeId;
                event.filter.parent_row_type = "P";
            }
        }
        this.textboxFieldValue.addBeforeLookupModelSearchListener(this.beforeSearchListener);
        return true;
    }

    carrierSalesRepValidation(): boolean {
        const brokerageOnlyValidation = this.allBrokerageValidation();
        if (!brokerageOnlyValidation) return false;

        this.beforeSearchListener = (event: LookupModelSearchEvent) => {
            event.filter.web_user_type = "U || E";
        }
        this.textboxFieldValue.addBeforeLookupModelSearchListener(this.beforeSearchListener);
        return true;
    }

    operationsUserValidation(): boolean {
        this.beforeSearchListener = (event: LookupModelSearchEvent) => {
            event.filter.web_user_type = "U || E";
        }
        this.textboxFieldValue.addBeforeLookupModelSearchListener(this.beforeSearchListener);
        return true;
    }

    movementTypeValidation(): boolean {
        const field = "brokerage";

        this.textboxFieldValue.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return {
                isValid: !StringUtil.isEmptyString(_value),
                component: this.textboxFieldValue,
                validationMessage: "A selection is required."
            }
        };

        const isBrokerage = this.selectedData[0].getBoolean(field, false);
        if (this.selectedData.some((row: ModelRow) => row.get(field, false) != isBrokerage)) {
            this.textboxFieldValue.disabledTooltip = "All selected movements must have the same movement type.";
            return false;
        }

        if (isBrokerage) {
            if (this.brokerageOnlyValidation()) {
                this.textboxFieldValue.selectedItem = (this.textboxFieldValue.items as DropdownItem[]).find(item => item.value === "Y");
                this.textboxFieldValue.disabledTooltip = "Cannot convert to asset in brokerage only company."
                return false;
            }
            const carrierField = "lme/dispatch/planning/csm/customer-service-movements" === this.modelPath ? "override_payee_id" : "carrier_id";
            if (this.selectedData.some((row: ModelRow) => !row.isNull(carrierField))) {
                this.textboxFieldValue.selectedItem = null;
                this.textboxFieldValue.disabledTooltip = "Cannot convert while carrier is assigned."
                return false;
            }
        } else {
            const tractorField = "lme/dispatch/planning/csm/customer-service-movements" === this.modelPath ? "tractor_number" : "tractor";
            if (this.selectedData.some((row: ModelRow) => !row.isNull("driver1_id")
                || !row.isNull("driver2_id") || !row.isNull(tractorField))) {
                this.textboxFieldValue.selectedItem = null;
                this.textboxFieldValue.disabledTooltip = "Cannot convert while tractor or driver is assigned."
                return false;
            }
        }

        return true;
    }

    movementTypeSetup() {
        const isBrokerage = this.selectedData[0].getBoolean("brokerage", false);
        const newType = isBrokerage ? "N" : "Y";
        this.textboxFieldValue.selectedItem = (this.textboxFieldValue.items as DropdownItem[]).find(item => item.value === newType);
        this.textboxFieldValue.enabled = false;
    }

    allBrokerageValidation(): boolean {
        const notBrokerage = (row: ModelRow) => !row.get("brokerage", false);
        if (this.selectedData.some(notBrokerage)) {
            this.textboxFieldValue.disabledTooltip = "All selected movements must be of type Brokerage.";
            return false;
        }
        return true;
    }

    brokerageOnlyValidation(): boolean {
        return CompanySettings.getSingleton().isBrokerage() && !CompanySettings.getSingleton().isBoth();
    }

    private mapFieldToLookup(field: string) {
        switch (field) {
            case Fields.BrokerageStatus: return BR_STATUS_LM;
            case Fields.RevenueCode: return REV_CODE_LM;
            case Fields.CarrierDispatchContact: return CARRIER_CONTACT_LM; //should update email and phone
            case Fields.CarrierSalesRep: return USER_LM;
            case Fields.OperationsUser: return USER_LM; //Order field that downstreams to the move
            case Fields.OrderType: return ORDER_TYPE_LM;
            case Fields.MovementType: return MOVEMENT_TYPE_OPTIONS;
            default: return FREEFORM;
        }
    }

    private mapFieldToMaxLength(field: string): number {
        switch (field) {
            case Fields.BOLNumber: return 28;
            case Fields.ConsigneeReferenceNumber: return 28;
            case Fields.PlanningComment: return 80;
            default: return 0;
        }
    }

    private getSwitchProps(field: string): Partial<SwitchProps> {
        switch (field) {
            default: return null;
        }
    }

    public getResults(): { field: string, value: any } {
        const field = this.textboxField.selectedItem?.value;
        if (this.isBooleanField(field)) {
            return {field, value: this.switchFieldValue.checked};
        } else {
            const value = (this.textboxFieldValue.lookupModel != null)
                ? this.textboxFieldValue.lookupModelData?.[0]?.getString("id")
                : this.textboxFieldValue.items?.length > 0
                ? this.textboxFieldValue.selectedItem?.value
                : this.textboxFieldValue.text
            return {field, value};
        }
    }

    get selectedData() {
        return this._selectedData;
    }

    set selectedData(data: ModelRow[]) {
        this._selectedData = data;
        this.modelPath = data.length > 0 ? data[0]._modelPath : null;
    }
}
